// frontend/src/utils/activityTracker.js

let heartbeatTimer = null;
let lastUserActivityTime = Date.now();
let isTracking = false;
let consecutiveFailures = 0;

// Configuration
const HEARTBEAT_INTERVAL = 2 * 60 * 1000;  // 2 minutes (ping every 2 min)
const MAX_CONSECUTIVE_FAILURES = 2;        // Redirect after 2 failed pings
const ACTIVITY_EVENTS = ['mousedown', 'keydown', 'scroll', 'touchstart', 'mousemove'];
const ACTIVITY_THROTTLE = 30 * 1000;       // Only log activity once per 30 sec

/**
 * Send heartbeat to maintain session.
 * Returns true if session is alive, false if expired.
 */
async function sendHeartbeat() {
  try {
    const response = await fetch('/api/auth/keepalive', {
      credentials: 'include'
    });
    
    if (response.ok) {
      consecutiveFailures = 0;
      console.debug('[Heartbeat] Session alive ✓');
      return true;
    } else if (response.status === 401) {
      console.warn('[Heartbeat] Session expired - redirecting to login');
      consecutiveFailures++;
      
      if (consecutiveFailures >= MAX_CONSECUTIVE_FAILURES) {
        handleSessionExpired();
      }
      return false;
    } else {
      console.error('[Heartbeat] Unexpected response:', response.status);
      return false;
    }
  } catch (error) {
    console.error('[Heartbeat] Network error:', error);
    // Don't count network errors as session expiry
    return true;
  }
}

/**
 * Handle session expiration - show toast and redirect.
 */
function handleSessionExpired() {
  stopActivityTracking();
  
  // Show toast if available
  const toast = window.chakraToast;
  if (toast) {
    toast({
      title: 'Session Expired',
      description: 'Your session has expired. Redirecting to login...',
      status: 'warning',
      duration: 3000,
      isClosable: true,
    });
  }
  
  // Clear storage and redirect
  localStorage.clear();
  sessionStorage.clear();
  
  setTimeout(() => {
    window.location.replace('/');
  }, 1000);
}

/**
 * Track user activity (debounced).
 */
function handleUserActivity() {
  const now = Date.now();
  
  // Only update if enough time passed (avoid excessive updates)
  if (now - lastUserActivityTime > ACTIVITY_THROTTLE) {
    lastUserActivityTime = now;
    console.debug('[Heartbeat] User activity detected');
  }
}

/**
 * Start the heartbeat system.
 * Sends periodic pings to maintain session while window is open.
 */
export function startActivityTracking() {
  if (isTracking) {
    console.debug('[Heartbeat] Already tracking');
    return;
  }
  
  isTracking = true;
  lastUserActivityTime = Date.now();
  consecutiveFailures = 0;
  
  console.info('[Heartbeat] Started - pinging every 2 minutes');
  
  // Listen for user activity
  ACTIVITY_EVENTS.forEach(event => {
    document.addEventListener(event, handleUserActivity, { passive: true });
  });
  
  // Send initial heartbeat
  sendHeartbeat();
  
  // Set up periodic heartbeat
  heartbeatTimer = setInterval(async () => {
    const sessionAlive = await sendHeartbeat();
    
    if (!sessionAlive) {
      console.warn('[Heartbeat] Session check failed');
    }
  }, HEARTBEAT_INTERVAL);
}

/**
 * Stop the heartbeat system.
 * Called on logout or unmount.
 */
export function stopActivityTracking() {
  if (!isTracking) return;
  
  isTracking = false;
  
  console.info('[Heartbeat] Stopped');
  
  // Remove event listeners
  ACTIVITY_EVENTS.forEach(event => {
    document.removeEventListener(event, handleUserActivity);
  });
  
  // Clear timer
  if (heartbeatTimer) {
    clearInterval(heartbeatTimer);
    heartbeatTimer = null;
  }
}

/**
 * Check if tracking is active (for debugging).
 */
export function isHeartbeatActive() {
  return isTracking;
}